import React from "react";
import { Modal } from "antd";
import { format } from "date-fns";

export const getPatientInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

export const getEnquiryStatusStyle = (status = "") => {
  const value = String(status).toUpperCase();
  if (value === "INPROGRESS") {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (value === "COMPLETED" || value === "CLOSED") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (value === "REJECTED" || value === "CANCELLED") {
    return "bg-red-50 text-red-600 border-red-200";
  }
  return "bg-slate-50 text-slate-600 border-slate-200";
};

export const orderTableHeadClass =
  "px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 border-b border-slate-200 bg-[#F6F8FB] whitespace-nowrap";
export const orderTableCellClass =
  "px-4 py-3 border-b border-slate-100 text-slate-800";

export function OrderDetailsModal({
  open,
  onClose,
  subtitle = "Order Details",
  patientName,
  status,
  infoFields = [],
  address,
  addressLabel = "Address",
  showAddress = true,
  width = 860,
  children,
}) {
  if (!open) return null;

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      width={width}
      centered
      closable
      className="order-details-modal"
      styles={{
        content: { padding: 0, borderRadius: 16, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      <div className="bg-gradient-to-r from-[#004172] to-[#3cba84] px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
              {getPatientInitials(patientName)}
            </span>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-white/75 mb-1">
                {subtitle}
              </p>
              <h3 className="text-xl font-bold text-white mb-0 truncate">
                {patientName || "—"}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {infoFields.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {infoFields.map((item) => (
              <div
                key={item.label}
                className={`rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 ${
                  item.fullWidth ? "sm:col-span-2 lg:col-span-3" : ""
                }`}
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-1">
                  {item.label}
                </p>
                <p
                  className={`text-sm font-semibold text-slate-900 mb-0 ${
                    item.mono ? "break-all font-mono text-xs" : ""
                  }`}
                >
                  {item.value || "—"}
                </p>
              </div>
            ))}
          </div>
        )}

        {showAddress && (
          <div className="rounded-xl border border-[#C5E8E3] bg-gradient-to-r from-[#E8F6F4] to-white px-4 py-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-[#0D6B6E] mb-1">
              {addressLabel}
            </p>
            <p className="text-sm font-medium text-slate-800 mb-0 leading-relaxed break-words">
              {address || "—"}
            </p>
          </div>
        )}

        {children}
      </div>
    </Modal>
  );
}

export function OrderDetailsSection({ title, children }) {
  return (
    <div>
      {title && (
        <h4 className="text-base font-semibold text-[#003F6B] mb-3">{title}</h4>
      )}
      {children}
    </div>
  );
}

export function OrderDetailsTable({ children }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-xl">
      <table className="w-full border-collapse text-sm min-w-[480px]">
        {children}
      </table>
    </div>
  );
}

export function SurgeryEnquiryCards({ surgeryEnquiry = [] }) {
  if (!surgeryEnquiry.length) return null;

  return (
    <OrderDetailsSection title="Surgery Details">
      <div className="space-y-3">
        {surgeryEnquiry.map((surgery, index) => (
          <div
            key={index}
            className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-[#F8FAFC] border-b border-slate-100">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 mb-0 truncate">
                  {surgery.procedureName || "Procedure not specified"}
                </p>
                <p className="text-xs text-slate-500 mb-0 mt-0.5">
                  {surgery.hospitalName || "Hospital not specified"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {surgery.serviceTypeName && (
                  <span className="px-2.5 py-1 rounded-md bg-[#E8F6F4] text-[#0D6B6E] text-xs font-medium">
                    {surgery.serviceTypeName}
                  </span>
                )}
                {surgery.serviceSubType && (
                  <span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                    {surgery.serviceSubType}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-4 py-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">Hospitalization Type</p>
                <p className="font-medium text-slate-900 mb-0">
                  {surgery.hospitalizationType || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Planned Start</p>
                <p className="font-medium text-slate-900 mb-0">
                  {surgery.procedurePlannedStartDate
                    ? format(
                        new Date(surgery.procedurePlannedStartDate),
                        "dd MMM yyyy"
                      )
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Planned End</p>
                <p className="font-medium text-slate-900 mb-0">
                  {surgery.procedurePlannedEndDate
                    ? format(
                        new Date(surgery.procedurePlannedEndDate),
                        "dd MMM yyyy"
                      )
                    : "—"}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </OrderDetailsSection>
  );
}

export const formatLabCollectionDate = (value) =>
  value
    ? new Date(value)
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replaceAll("/", "-")
    : "—";

export default OrderDetailsModal;
